import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import BaggageAccordion from './BaggageAccordion';
import { useColors } from 'frontend/hooks/useColors';

const BaggageForm = ({
  flightData,
  originCityName,
  originCode,
  destinationCityName,
  destinationCode,
  services,
  onBaggageSelect,
}) => {
  if (!flightData) return <></>;

  const [passengersBaggage, setPassengersBaggage] = useState([]);
  const [expandedPassengers, setExpandedPassengers] = useState([]);
  const [selectedBaggage, setSelectedBaggage] = useState({});
  const { background } = useColors();

  // Crear mapeo de segmentos
  const segmentMapping = React.useMemo(() => {
    const mapping = {
      outbound: [],
      return: [],
    };

    let segmentCount = 1;

    flightData.journeys.outbound.segments.forEach(() => {
      mapping.outbound.push(segmentCount.toString());
      segmentCount++;
    });

    if (flightData.journeys.return) {
      flightData.journeys.return.segments.forEach(() => {
        mapping.return.push(segmentCount.toString());
        segmentCount++;
      });
    }

    return mapping;
  }, [flightData]);

  // Función para validar si un supplement debe mostrarse
  const isValidSupplement = (supplement) => {
    return supplement.short_name && ['INC', 'NOF'].includes(supplement.indicator);
  };

  // Función para deduplicar equipaje con la nueva validación
  const deduplicateBaggage = (supplements) => {
    const uniqueBaggage = new Map();

    supplements.forEach((supplement) => {
      // Solo procesar supplements válidos
      if (!isValidSupplement(supplement)) return;

      const key = `${supplement.category}-${supplement.short_name}`;

      if (uniqueBaggage.has(key)) {
        const existing = uniqueBaggage.get(key);
        if ((supplement.quantity || 1) > (existing.quantity || 1)) {
          uniqueBaggage.set(key, supplement);
        }
      } else {
        uniqueBaggage.set(key, supplement);
      }
    });

    return Array.from(uniqueBaggage.values());
  };

  // Procesar supplements por journey con deduplicación y validación
  const supplementsByJourney = React.useMemo(() => {
    if (!flightData.supplements) return {};

    const result = {
      [flightData.journeys.outbound.id]: [],
      [flightData.journeys.return?.id]: [],
    };

    const outboundSupplements = [];
    const returnSupplements = [];

    flightData.supplements.forEach((supplement) => {
      if (
        (supplement.category === 'Baggage' || supplement.category === 'HandBaggage') &&
        isValidSupplement(supplement)
      ) {
        const isOutbound = supplement.seg_references.some((ref) =>
          segmentMapping.outbound.includes(ref),
        );
        const isReturn = supplement.seg_references.some((ref) =>
          segmentMapping.return.includes(ref),
        );

        if (isOutbound) {
          outboundSupplements.push({
            ...supplement,
            pax_references: supplement.pax_references || ['ADT', 'CHD', 'INF'],
          });
        }

        if (isReturn && flightData.journeys.return) {
          returnSupplements.push({
            ...supplement,
            pax_references: supplement.pax_references || ['ADT', 'CHD', 'INF'],
          });
        }
      }
    });

    result[flightData.journeys.outbound.id] = deduplicateBaggage(outboundSupplements);
    if (flightData.journeys.return) {
      result[flightData.journeys.return.id] = deduplicateBaggage(returnSupplements);
    }

    return result;
  }, [flightData, segmentMapping]);

  // Process services
  const servicesByJourney = React.useMemo(() => {
    if (!services?.solutions?.[0]?.services) return {};

    const availableServices = services.solutions[0].services;

    return availableServices.reduce((acc, service) => {
      if (Array.isArray(service.journey_references)) {
        service.journey_references.forEach((journeyRef) => {
          if (!acc[journeyRef]) {
            acc[journeyRef] = {};
          }

          service.pax_references.forEach((paxRef) => {
            if (!acc[journeyRef][paxRef]) {
              acc[journeyRef][paxRef] = [];
            }
            acc[journeyRef][paxRef].push(service);
          });
        });
      }
      return acc;
    }, {});
  }, [services]);

  const createPassengerData = (id, number, type, paxRef) => {
    const paxType = services?.solutions?.[0]?.paxes_types[paxRef];

    return {
      id,
      number,
      type,
      paxRef,
      routes: [
        {
          direction: 'Ida',
          route: `${originCityName} (${originCode}) - ${destinationCityName} (${destinationCode})`,
          journeyRef: flightData.journeys.outbound.id,
          baggage: supplementsByJourney[flightData.journeys.outbound.id].filter((sup) =>
            sup.pax_references.includes(paxType),
          ),
        },
        flightData.journeys.return && {
          direction: 'Vuelta',
          route: `${destinationCityName} (${destinationCode}) - ${originCityName} (${originCode})`,
          journeyRef: flightData.journeys.return.id,
          baggage: supplementsByJourney[flightData.journeys.return.id].filter((sup) =>
            sup.pax_references.includes(paxType),
          ),
        },
      ].filter(Boolean),
    };
  };

  // Rest of the component remains the same...
  useEffect(() => {
    const initialPassengers = [];
    let passengerCount = 0;
    const paxTypes = services?.solutions?.[0]?.paxes_types || {};

    // Process adult passengers
    if (flightData.passengers.adults) {
      const adultRefs = Object.entries(paxTypes)
        .filter(([_, type]) => type === 'ADT')
        .map(([ref]) => ref);

      for (let i = 0; i < flightData.passengers.adults.quantity; i++) {
        const passengerId = `adult_${i}`;
        initialPassengers.push(
          createPassengerData(passengerId, ++passengerCount, 'Adulto', adultRefs[i]),
        );
      }
    }

    // Process child passengers
    if (flightData.passengers.children) {
      const childRefs = Object.entries(paxTypes)
        .filter(([_, type]) => type === 'CHD')
        .map(([ref]) => ref);

      for (let i = 0; i < flightData.passengers.children.quantity; i++) {
        const passengerId = `child_${i}`;
        initialPassengers.push(
          createPassengerData(passengerId, ++passengerCount, 'Niño', childRefs[i]),
        );
      }
    }

    // Process infant passengers
    if (flightData.passengers.baby) {
      const infantRefs = Object.entries(paxTypes)
        .filter(([_, type]) => type === 'INF')
        .map(([ref]) => ref);

      for (let i = 0; i < flightData.passengers.baby.quantity; i++) {
        const passengerId = `infant_${i}`;
        initialPassengers.push(
          createPassengerData(passengerId, ++passengerCount, 'Bebé', infantRefs[i]),
        );
      }
    }

    setPassengersBaggage(initialPassengers);
    setExpandedPassengers(initialPassengers.map((_, index) => index === 0));
  }, [flightData, services]);

  useEffect(() => {
    onBaggageSelect?.(selectedBaggage);
  }, [selectedBaggage, onBaggageSelect]);

  const handleAccordionChange = (index) => {
    setExpandedPassengers((prev) => {
      const newExpanded = [...prev];
      newExpanded[index] = !newExpanded[index];
      return newExpanded;
    });
  };

  const handleBaggageSelection = (passengerId, journeyRef, serviceId, isSelected) => {
    setSelectedBaggage((prev) => {
      const newSelection = { ...prev };

      // Encontrar el servicio completo
      const availableServices = services?.solutions?.[0]?.services || [];
      const selectedService = availableServices.find((service) => service.id === serviceId);

      if (!selectedService) return prev;

      if (!newSelection[passengerId]) {
        newSelection[passengerId] = [];
      }

      if (isSelected) {
        // Extraer solo el número del passengerId (ejemplo: de 'adult_1' obtener '1')
        const paxNumber = passengerId.split('_')[1];

        // Agregar el servicio completo con la información necesaria
        newSelection[passengerId].push({
          id: selectedService.id,
          short_name: selectedService.short_name,
          currency: selectedService.currency,
          base: selectedService.base,
          fee: selectedService.fee,
          price: selectedService.price,
          total: selectedService.total,
          quantity: 1,
          pax_references: [paxNumber], // Solo el número del pasajero
          seg_references: selectedService.seg_references || [],
          journey_references: [journeyRef],
        });
      } else {
        // Remover el servicio si está deseleccionado
        newSelection[passengerId] = newSelection[passengerId].filter(
          (service) => service.id !== serviceId,
        );
      }

      // Limpiar el pasajero si no tiene servicios seleccionados
      if (newSelection[passengerId].length === 0) {
        delete newSelection[passengerId];
      }

      return newSelection;
    });
  };

  useEffect(() => {
    onBaggageSelect?.(selectedBaggage);
  }, [selectedBaggage, onBaggageSelect]);

  return (
    <Box sx={{ py: 2 }}>
      {passengersBaggage.map((passenger, index) => (
        <BaggageAccordion
          key={passenger.id}
          borderColor={background}
          passenger={passenger}
          expanded={expandedPassengers[index]}
          onChange={() => handleAccordionChange(index)}
          services={Object.fromEntries(
            Object.entries(servicesByJourney).map(([journeyRef, paxServices]) => [
              journeyRef,
              paxServices[passenger.paxRef] || [],
            ]),
          )}
          selectedBaggage={
            selectedBaggage[passenger.id]?.reduce((acc, service) => {
              if (!acc[service.journey_references[0]]) {
                acc[service.journey_references[0]] = [];
              }
              acc[service.journey_references[0]].push(service.id);
              return acc;
            }, {}) || {}
          }
          onBaggageSelect={handleBaggageSelection}
        />
      ))}
    </Box>
  );
};

export default BaggageForm;
